import { toast } from './toast';

/**
 * The one way a form says "this cannot be saved yet": a toast that is always on screen
 * (whatever the scroll position) listing every problem, then the page scrolls to the first
 * field marked invalid (aria-invalid / data-invalid) — or to `anchor`, the inline error box.
 */
export function reportProblems(problems: string[], opts: { title?: string; root?: ParentNode | null; anchor?: Element | null } = {}) {
  if (!problems.length) return;
  const title = opts.title ?? (problems.length === 1 ? 'Cannot save yet' : `Cannot save yet — ${problems.length} things to fix`);
  toast.error(title, {
    id: 'form-problems',
    duration: 8000,
    description: (
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        {problems.slice(0, 6).map((p) => <li key={p}>{p}</li>)}
        {problems.length > 6 && <li>…and {problems.length - 6} more (listed on the form)</li>}
      </ul>
    ),
  });
  scrollToFirstProblem(opts.root, opts.anchor);
}

/** Waits for the error state to render, then brings the first invalid field (else `anchor`) into view and focuses it. */
export function scrollToFirstProblem(root?: ParentNode | null, anchor?: Element | null) {
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const el = (root ?? document).querySelector<HTMLElement>('[aria-invalid="true"], [data-invalid="true"]') ?? (anchor as HTMLElement | null);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const focusable = el.matches('input, select, textarea, button') ? el : el.querySelector<HTMLElement>('input:not([type=hidden]), select, textarea, button');
      focusable?.focus({ preventScroll: true });
    }),
  );
}

/** A refused action (approve, post, cancel…): always a toast, never a silent no-op. */
export function reportFailure(title: string, e: unknown) {
  toast.error(title, { description: e instanceof Error ? e.message : String(e ?? 'Something went wrong. Please try again.') });
}

/** Sets a dialog's inline error and also toasts it, so it is seen even when the box is out of view. */
export function flag<T extends string | null>(set: (m: T) => void, message: T) {
  set(message);
  if (message) toast.error(message, { id: 'form-problems' });
}
